import { Card, Form } from "react-bootstrap"
import { useLocalizedStrings } from "../Localization";
import { useRef, useState, useContext, useEffect, useMemo } from "react";
import { useAPIUpload, usePost } from "../API";
import { Button, Checkbox, useToast, Icon, Spinner, DataTable } from "@aidkitorg/component-library";
import { copyToClipboard } from "../Util";
import InterfaceContext, { ConfigurationContext, SupportedLanguage, UserInfoContext } from "../Context";
import { langToWord } from "../Localization";
import { ColumnDef } from "@tanstack/react-table";

type PublicS3FileData = {
  key: string;
  url: string;
  filename: string;
  size: number;
  lastModified: string;
  description?: string;
  uploadedBy?: string;
};

export default function PublicS3() {

  const L = useLocalizedStrings();
  const fileInput = useRef(null as any);
  const audioFileInput = useRef(null as any);
  const [fileInputLabel, setFileInputLabel] = useState(L.questions.attachment.choose_files);
  const [audioFileInputLabel, setAudioFileInputLabel] = useState(L.questions.attachment.choose_files);
  const [s3URL, setS3URL] = useState('');
  const [audioS3URL, setAudioS3URL] = useState('');
  const [lang, setLang] = useState<SupportedLanguage>('en');

  const [shortlinkFileUrl, setShortlinkFileUrl] = useState('');
  const [shortlinkSlug, setShortlinkSlug] = useState('')
  const [validSlug, setValidSlug] = useState(false)

  const [shortlinkURL, setShortlinkURL] = useState('')

  const [shortlinkCreationStatus, setShortlinkCreationStatus] = useState<'' | 'success' | 'error'>('')
  const [shortlinkErrorMsg, setShortlinkErrorMsg] = useState('')

  const [overrideShortlinkCode, setOverrideShortlinkCode] = useState(false)

  // File upload tracking state
  const [description, setDescription] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<PublicS3FileData[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showOnlyMyUploads, setShowOnlyMyUploads] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);

  const uploadURL = usePost("/document/upload_url");
  const upload = useAPIUpload("/upload");
  const createShortlink = usePost("/document/create_shortlink");
  const listS3Files = usePost('/document/public_s3/list');
  const invalidateCache = usePost('/document/public_s3/invalidate_cache');

  const context = useContext(InterfaceContext);
  const configuration = useContext(ConfigurationContext);
  const userInfo = useContext(UserInfoContext);

  const { toast } = useToast();

  useEffect(() => {
    loadUploadedFiles();
  }, []);

  async function loadUploadedFiles() {
    setLoadingFiles(true);
    try {
      const result = await listS3Files({});
      if (result.files) {
        setUploadedFiles(result.files);
      }
    } catch (error) {
      console.error('Error loading files from S3:', error);
      toast({
        description: 'Failed to load files',
        variant: 'error'
      });
    } finally {
      setLoadingFiles(false);
    }
  }


  async function uploadGeneralFile() {
    if (!selectedFile) return;

    const url = await uploadURL({
      path: selectedFile.name,
      length: selectedFile.size,
      publicFile: true,
      description: description
    });

    let result;

    try {
      result = await upload([selectedFile] as unknown as any[], {}, url.uploadURL, "PUT");
      setS3URL(url.savedPath!);
    } catch (e) {
      console.warn("Error uploading file: ", e);
      setSelectedFile(null);
      if (fileInput.current) {
        fileInput.current.value = "";
        setFileInputLabel(L.questions.attachment.choose_file);
      }
      toast({
        description: "Error uploading file",
        variant: 'error'
      });
      return;
    }

    if (typeof result === 'string' && url.savedPath) {
      setDescription('');
      setSelectedFile(null);
      await invalidateCache({});
      await loadUploadedFiles();

      toast({
        description: "Upload successful",
        variant: 'success'
      });

      if (fileInput.current) {
        fileInput.current.value = "";
        setFileInputLabel(L.questions.attachment.choose_file);
      }
    }
  }

  async function uploadAudioFile(files: File[]) {
    if (!lang) {
      toast({
        description: "Language code is required for audio uploads.",
        variant: 'error'
      });
      return;
    }

    const url = await uploadURL({
      path: `tts/${lang}/${files[0].name}`,
      length: files[0].size,
      publicFile: true
    });

    let result;

    try {
      result = await upload(files as unknown as any[], {}, url.uploadURL, "PUT");
      setAudioS3URL(url.savedPath!);
    } catch (e) {
      console.warn("Error uploading file: ", e);
      toast({
        description: "Error uploading file",
        variant: 'error'
      });
      return;
    }

    if (typeof result === 'string' && url.savedPath) {
      await invalidateCache({});
      await loadUploadedFiles();

      toast({
        description: "Upload successful",
        variant: 'success'
      });

      if (audioFileInput.current) {
        audioFileInput.current.value = "";
        setAudioFileInputLabel(L.questions.attachment.choose_file);
      }
    }
  } 

  const createShortlinkFromFile = async () => {
    const result = await createShortlink({
      file: shortlinkFileUrl,
      slug: shortlinkSlug,
      override: overrideShortlinkCode,
    })

    if (result.status === "ok" && result.shortlink) {
      setShortlinkCreationStatus('success')
      setShortlinkURL(result.shortlink)
    } else {
      setShortlinkCreationStatus('error')
      setShortlinkErrorMsg(result?.message || "")
    }
  }

  // Validity Rules for Shortlink Slugs
  // Shortlink slugs must more than 8 characters in length (to prevent clashing with any auto-generated slugs)
  // Shortlink slugs must be alphanumeric
  useEffect(() => {
    if (shortlinkSlug.length > 8 &&  /^[0-9A-Z]+$/i.test(shortlinkSlug)) {
      setValidSlug(true)
    } else {
      setValidSlug(false)
    }

  }, [shortlinkSlug])

  const shortlinkStatusMessage = () => {
    if (shortlinkCreationStatus === 'success') {
      return (
        <Card style={{ width: '30rem', padding: '2rem', backgroundColor: "lavender" }}>
          <Card.Text id='url'>
            Successfully created shortlink at: 
            <Button variant="link" href={shortlinkURL}>{shortlinkURL}</Button>
          </Card.Text> 
          <Button variant="secondary" onClick={() => { copyToClipboard(shortlinkURL); toast({ description: L.copied_to_clipboard, variant: 'success' }); }}>
            Copy URL to Clipboard
          </Button> 
        </Card>
      )
    } else if (shortlinkCreationStatus === 'error') {
      return (
        <Card 
          className="!bg-red-200"
          style={{ width: '30rem', padding: '1rem' }}>
          <Card.Text>
            Error creating shortlink: {shortlinkErrorMsg}
          </Card.Text> 
        </Card>
      )
    }
  }

  const filteredFiles = useMemo(() => {
    let filtered = uploadedFiles;

    if (showOnlyMyUploads && userInfo.uid) {
      filtered = filtered.filter(file => file.uploadedBy === userInfo.uid);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(file =>
        file.filename?.toLowerCase().includes(query) ||
        file.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [uploadedFiles, showOnlyMyUploads, userInfo.uid, searchQuery]);

  const columns = useMemo<ColumnDef<PublicS3FileData>[]>(() => [
    {
      accessorKey: 'filename',
      header: 'File Name',
      cell: ({ row }) => (
        <div className="font-semibold truncate" title={row.original.filename}>
          {row.original.filename}
        </div>
      ),
      size: 300,
    },
    {
      accessorKey: 'description',
      header: 'Description',
      cell: ({ row }) => (
        <div className="truncate" title={row.original.description || ''}>
          {row.original.description || <span className="text-muted">-</span>}
        </div>
      ),
      size: 350,
    },
    {
      accessorKey: 'lastModified',
      header: 'Uploaded Date',
      cell: ({ row }) => new Date(row.original.lastModified).toLocaleDateString(),
      size: 150,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            copyToClipboard(row.original.url);
            toast({ description: 'URL copied to clipboard!', variant: 'success' });
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '6px' }}>
            <Icon name="LinkIcon" />
            <span>Copy Link</span>
          </div>
        </Button>
      ),
      size: 200,
      enableSorting: false,
    },
  ], []);

  return(
    <>
      <h5>Upload a general document file to store on S3 and create a publicly accessible URL.</h5>
      <div>
        <Form>
          <div className="row">
            <div className="col">
              <Form.Group>
                <div className="custom-file">
                  <input
                    multiple
                    className="custom-file-input"
                    id={"fileupload"}
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        setSelectedFile(e.target.files[0]);
                        setFileInputLabel(e.target.files[0].name);
                      } else {
                        setSelectedFile(null);
                        setFileInputLabel(L.questions.attachment.choose_file);
                      }
                    }}
                    type="file"
                    ref={fileInput}
                  />
                  <label
                    className={"custom-file-label"}
                    htmlFor={"fileupload"}
                  >
                    {fileInputLabel}
                  </label>
                </div>
              </Form.Group>
              <Form.Group className="mt-3">
                <Form.Label>Description (optional)</Form.Label>
                <Form.Control
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g., Application form template"
                />
              </Form.Group>
              <Button
                className="mt-3"
                onClick={uploadGeneralFile}
                isDisabled={!selectedFile}
              >
                Upload
              </Button>
            </div>
          </div>
        </Form>
        <div hidden={!s3URL}>
          <div className="d-flex justify-content-center m-3">
            <Card style={{ width: '30rem', padding: '2rem', backgroundColor: "lavender" }}>
              <Card.Title>
                Public-Access URL:
              </Card.Title>
              <Card.Text id='url'>
                {s3URL}
              </Card.Text> 
              <Button variant="secondary" onClick={() => { copyToClipboard(s3URL); toast({ description: L.copied_to_clipboard, variant: 'success' }); }}>
                Copy URL to Clipboard
              </Button> 
            </Card>
          </div>
        </div>

        {/* Uploaded Files List */}
        <div className="mt-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6>Previously Uploaded Files</h6>
          </div>

          {/* Search and Filter Controls */}
          <div className="mb-3">
            <Form.Group className="mb-2">
              <Form.Control
                type="text"
                placeholder="Search by filename or description..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Form.Group>
            <div className="d-flex gap-2 align-items-center">
              <Checkbox
                checked={showOnlyMyUploads}
                onChange={(v) => {
                  if (v) {
                    setShowOnlyMyUploads(true)
                  }
                  else {
                    setShowOnlyMyUploads(false)
                  }
                }}
                label="Only show my uploads"
              />
            </div>
          </div>

          {loadingFiles ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <Spinner size="small" />
              <span className="ms-2">Loading files...</span>
            </div>
          ) : (
            <DataTable
              data={filteredFiles}
              columns={columns}
              pageSize={5}
              noDataMessage="No files found."
              getRowId={(row) => row.key}
              initialSorting={[{ id: 'lastModified', desc: true }]}
            />
          )}
        </div>
      </div>
      <hr/>
      <h5>Custom Shortlink</h5>
      <div>
        <div>
          Shortlink codes must be at least 9 characters and only contain numbers and letters.
        </div>
        <div className="py-2">
          <div>
            <label>
              URL:
              <input
                className="ml-2 px-2 border rounded-md"
                value={shortlinkFileUrl}
                onChange={(e) => setShortlinkFileUrl(e.target.value)}
              />
            </label>
          </div>
          <div>
            <label>
              Shortlink Code:
              <input 
                className="ml-2 px-2 border rounded-md"
                onChange={(e) => setShortlinkSlug(e.target.value)}
              />
            </label>
          </div>
        </div>
        <div>
          Your shortlink will look like: {`${window.location.origin}/sl/${shortlinkSlug}`}
        </div>
        <div className="py-2">
          <Checkbox 
            checked={overrideShortlinkCode}
            onChange={
              (v) => {
                if (v) { 
                  setOverrideShortlinkCode(true) 
                }
                else {
                  setOverrideShortlinkCode(false)
                }
              }
            } 
            label="Override existing shortlinks"/>
        </div>
        <div>
          {
            overrideShortlinkCode && 
            <Card 
              className="!bg-red-200"
              style={{ width: '40rem', padding: '1rem' }}>
              <Card.Text>
                This will override any existing shortlinks created with the same shortlink code.
              </Card.Text> 
            </Card>
          }
        </div>
        <div>
          <Button className="my-2" isDisabled={!validSlug} onClick={async () => createShortlinkFromFile()}>Create Shortlink</Button>        
        </div>
        <div className="d-flex justify-content-center m-3">
          {shortlinkStatusMessage()}
        </div>
      </div>
      <hr/>
      <h5>Upload an audio file to store for custom text-to-speech.</h5>
      <div>
        <Form>
          <div className="row">
            <div className="col">
              <Form.Group>
                <Form.Label>Language:</Form.Label>
                <Form.Control
                  as="select"
                  value={lang}
                  onChange={(e) => setLang(e.target.value as SupportedLanguage)}
                >
                  {(configuration.languages || 'en,es').split(',').map((language: string) => (
                    <option key={language} value={language}>
                      {langToWord(language as SupportedLanguage)}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group>
                <div className="custom-file">
                  <input
                    multiple
                    className="custom-file-input"
                    id={"audioFileUpload"}
                    onChange={async (e) => {
                      if (e.target.files && e.target.files.length === 0) {
                        setAudioFileInputLabel(L.questions.attachment.choose_file);
                      } else {
                        if (e.target.files) {
                          uploadAudioFile(Array.from(e.target.files));
                          setAudioFileInputLabel(L.questions.attachment.choose_file);
                        }
                      }
                    }}
                    type="file"
                    ref={audioFileInput}
                  />
                  <label
                    className={"custom-file-label"}
                    htmlFor={"audioFileUpload"}
                  >
                    {audioFileInputLabel}
                  </label>
                </div>
              </Form.Group>
            </div>
          </div>
        </Form>
        <div hidden={!audioS3URL}>
          <div className="d-flex justify-content-center m-3">
            <Card style={{ width: '30rem', padding: '2rem', backgroundColor: "lavender" }}>
              <Card.Title>
                Custom Text-to-Speech URL:
              </Card.Title>
              <Card.Text id='url'>
                {audioS3URL}
              </Card.Text> 
              <Button variant="secondary" onClick={() => { copyToClipboard(audioS3URL); toast({ description: L.copied_to_clipboard, variant: 'success' }) }}>
                Copy URL to Clipboard
              </Button> 
            </Card>
          </div>
        </div>
      </div>
    </>
  )
}
